import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { setCompanyApiEnabled } from '../../../../api/firebase/function/apiKeyFunction';
import {
  gray40,
  orange50,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { dialogText, dialogTitle, regularSm } from '../../../../constants/location';
import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import CustomDatePicker from '../../../uiParts/form/CustomDatePicker';

/** 開始日として有効な日付か */
const isValidStartDate = (value: Date | null): boolean => {
  if (!value) return false;
  return !Number.isNaN(value.getTime());
};

interface Props {
  open: boolean;
  companyId: string;
  /** 変更前の有効状態。ダイアログはこの反対の状態へ切り替える前提で開く。 */
  currentEnabled: boolean;
  onClose: () => void;
  /** 切替成功時に新しい有効状態を返す。 */
  onChanged: (after: boolean) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bodyText: {
    ...dialogText,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
  },
  checkboxLabel: {
    ...regularSm,
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const ApiFeatureToggleDialog: React.FC<Props> = ({
  open,
  companyId,
  currentEnabled,
  onClose,
  onChanged,
}) => {
  const pendingState = !currentEnabled;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checkStartDate, setCheckStartDate] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError('');
      setCheckStartDate(false);
      setStartDate(null);
      setNote('');
    }
  }, [open]);

  const canConfirm = pendingState ? checkStartDate && isValidStartDate(startDate) : true;

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      const startDateText = startDate ? dayjs(startDate).format('YYYY-MM-DD') : '';
      const composedNote = pendingState
        ? `開始日: ${startDateText}${note ? ` / 備考: ${note}` : ''}`
        : note || '無効化';
      await setCompanyApiEnabled({
        company_id: companyId,
        enabled: pendingState,
        start_date: pendingState ? startDateText : undefined,
        note: composedNote,
      });
      onChanged(pendingState);
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : '切替に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          API 機能を{pendingState ? '有効化' : '無効化'}しますか？
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.fieldWrapper}>
        {error && <Box sx={styles.errorBox}>{error}</Box>}

        {pendingState ? (
          <>
            <Typography sx={styles.bodyText}>有効化前に開始日を確認してください。</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkStartDate}
                  onChange={(e): void => setCheckStartDate(e.target.checked)}
                  sx={{ '&.Mui-checked': { color: orange50 } }}
                />
              }
              label={
                <Typography sx={styles.checkboxLabel}>開始日について顧客と合意済みです</Typography>
              }
            />
            <CustomDatePicker
              labelText="開始日"
              requiredText="必須"
              isRequired
              value={startDate}
              onChange={(value): void => setStartDate(value)}
              width="100%"
              fontSizeIs14px
              labelFontSize="14px"
              formMb="zero"
              hideHelperText
            />
            <CustomTextfield
              labelText="備考 (任意)"
              value={note}
              onChange={(e): void => setNote(e.target.value)}
              multiline
              minRows={2}
              inputWidth="100%"
              fontSizeIs14px
              formMb="0"
            />
          </>
        ) : (
          <>
            <Typography sx={styles.bodyText}>
              無効化すると企業の API キーは即座に 401 を返すようになります。
              顧客への通知漏れに注意してください。
            </Typography>
            <CustomTextfield
              labelText="備考 (任意)"
              value={note}
              onChange={(e): void => setNote(e.target.value)}
              multiline
              minRows={2}
              inputWidth="100%"
              fontSizeIs14px
              formMb="0"
            />
          </>
        )}

        <Box sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || !canConfirm}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            {pendingState ? '有効化する' : '無効化する'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ApiFeatureToggleDialog;
